import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Autocomplete,
  CircularProgress,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
  Receipt as ChequeIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Pending as PendingIcon,
  CheckCircle as PrintedIcon,
  CheckCircle as CheckCircleIcon,
  ViewList as AllIcon,
  Cancel as CancelIcon,
  HourglassEmpty as UnclearedIcon,
  Unpublished as ReverseClearIcon,
  AttachFile as AttachFileIcon,
  History as HistoryIcon,
  DriveFileRenameOutline as SignedIcon,
  HowToReg as CollectedIcon,
  Undo as UndoSignIcon,
  ReceiptLong as VoucherIcon,
  CloudDownload as ZohoIcon,
} from '@mui/icons-material';
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridColumnMenu
} from '@mui/x-data-grid';
import { exportToExcel } from '../../../utils/excelExport';
import { EasyCheckButton, EasyCheckToggleButton, EasyCheckIconButton, EasyCheckDatePicker, EasyCheckDialog, EasyCheckTextField, EasyCheckDataGridBasic } from '../../common/Controls';
import {
  PrintOptionsDialog,
  PrintConfigurationDisplay,
  usePrintConfiguration
} from '../../common/Printing';
import { formatDateForAPI, formatDateForDisplay } from '../../../utils/dateUtils';
import { validateChequeNumber, formatChequeNumber } from '../../../utils/chequeValidators';
import { getAmountStep, validateAmountPrecision, formatCurrencyAmount, getCurrencyDecimals } from '../../../utils/currencyUtils';
import { styled, useTheme } from '@mui/material/styles';
import { useForm, Controller } from 'react-hook-form';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';
import { chequeService } from '../../../services/cheques/chequeService';
import { voucherService } from '../../../services/cheques/voucherService';
import { useBankService } from '../../../services/banks/bankService';
import { useBeneficiaryService } from '../../../services/beneficiaries/beneficiaryService';
import { companyPreferencesService } from '../../../services/settings/companyPreferencesService';
import { useCheques } from '../../../hooks/useCheques';
import { useSnackbar } from '../../../hooks/useSnackbar';
import { useEasyCheck } from '../../../context/EasyCheckContext';
import SnackbarNotification from '../../common/Feedback/SnackbarNotification';
import { themeUtils } from '../../../theme';
import ClearingDialog from './ClearingDialog';
import ChequeAttachments from './ChequeAttachments';
import ChequeHistoryImportDialog from './ChequeHistoryImportDialog';
import ZohoImportDialog from './ZohoImportDialog';
import { zohoService } from '../../../services/zoho/zohoService';
import { canAccess } from '../../../utils/planPermissions';

const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.2, 1, 0.2, 1),
  height: 'calc(100vh - 100px)',
  maxHeight: 'calc(100vh - 100px)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
  backgroundColor: theme.palette.background.default,
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(0.5),
  padding: theme.spacing(0.75, 0),
  flexShrink: 0,
}));

// Custom toolbar extracted outside component to prevent re-mount on state changes
const CustomToolbar = ({ searchQuery, onSearchChange, statusFilter, onStatusFilterChange, selectedRows, onClearingDialog, onUnclearDialog, onSignCheques, onUnsignCheques, signedStatusEnabled, chequeCollectionEnabled, onExportExcel, onImportHistory, zohoImportAvailable, onImportZoho }) => {
  const statusOptions = [
    { value: 'all', label: 'All', icon: <AllIcon fontSize="small" /> },
    { value: 'pending', label: 'Pending', icon: <PendingIcon fontSize="small" /> },
    { value: 'printed', label: 'Printed', icon: <PrintedIcon fontSize="small" /> },
    { value: 'uncleared', label: 'Uncleared', icon: <UnclearedIcon fontSize="small" /> },
    ...(signedStatusEnabled ? [{ value: 'signed', label: 'Signed', icon: <SignedIcon fontSize="small" /> }] : []),
    ...(chequeCollectionEnabled ? [{ value: 'collected', label: 'Collected', icon: <CollectedIcon fontSize="small" /> }] : []),
    { value: 'cleared', label: 'Cleared', icon: <CheckCircleIcon fontSize="small" /> },
    { value: 'cancelled', label: 'Cancelled', icon: <CancelIcon fontSize="small" /> }
  ];

  return (
    <GridToolbarContainer sx={{ justifyContent: 'space-between', p: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GridToolbarColumnsButton
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            '&:hover': {
              borderColor: 'primary.main'
            }
          }}
        />
        <EasyCheckButton
          buttonType="action"
          variant="outlined"
          size="small"
          startIcon={<HistoryIcon />}
          onClick={onImportHistory}
        >
          Import
        </EasyCheckButton>
        {zohoImportAvailable && (
          <EasyCheckButton
            buttonType="action"
            variant="outlined"
            size="small"
            startIcon={<ZohoIcon />}
            onClick={onImportZoho}
          >
            From Zoho
          </EasyCheckButton>
        )}
        <EasyCheckButton
          buttonType="action"
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon />}
          onClick={onExportExcel}
        >
          Export
        </EasyCheckButton>
        <EasyCheckToggleButton
          value={statusFilter}
          onChange={(event, newValue) => {
            if (newValue !== null) {
              onStatusFilterChange(newValue);
            }
          }}
          options={statusOptions}
          buttonType="action"
          size="small"
          sx={{ ml: 1 }}
        />
        <EasyCheckTextField
          variant="compact"
          size="small"
          placeholder="Search cheques..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ ml: 1, width: 220 }}
        />
        {selectedRows.length > 0 && ['printed', 'uncleared', 'signed', 'collected'].includes(statusFilter) && (
          <EasyCheckButton
            buttonType="action"
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={() => onClearingDialog(selectedRows)}
            size="small"
            sx={{ ml: 1 }}
          >
            Mark Cleared ({selectedRows.length})
          </EasyCheckButton>
        )}
        {selectedRows.length > 0 && statusFilter === 'cleared' && (
          <EasyCheckButton
            buttonType="action"
            variant="contained"
            color="primary"
            startIcon={<ReverseClearIcon />}
            onClick={() => onUnclearDialog(selectedRows)}
            size="small"
            sx={{ ml: 1 }}
          >
            Mark Uncleared ({selectedRows.length})
          </EasyCheckButton>
        )}
        {signedStatusEnabled && selectedRows.length > 0 && (statusFilter === 'printed' || statusFilter === 'uncleared') && (
          <EasyCheckButton
            buttonType="action"
            variant="contained"
            color="primary"
            startIcon={<SignedIcon />}
            onClick={() => onSignCheques(selectedRows)}
            size="small"
            sx={{ ml: 1 }}
          >
            Mark Signed ({selectedRows.length})
          </EasyCheckButton>
        )}
        {signedStatusEnabled && selectedRows.length > 0 && statusFilter === 'signed' && (
          <EasyCheckButton
            buttonType="action"
            variant="contained"
            color="primary"
            startIcon={<UndoSignIcon />}
            onClick={() => onUnsignCheques(selectedRows)}
            size="small"
            sx={{ ml: 1 }}
          >
            Revert to Printed ({selectedRows.length})
          </EasyCheckButton>
        )}
      </Box>
    </GridToolbarContainer>
  );
};

const ChequePrintingForm = ({ onSuccess }) => {
  const theme = useTheme();
  
  // Fetch company preferences to use as print config defaults
  const { data: companyPreferences } = useQuery({
    queryKey: ['companyPreferences'],
    queryFn: async () => {
      const result = await companyPreferencesService.getPreferences();
      return result.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Use the print configuration hook with company defaults
  const { printConfig, updatePrintConfig } = usePrintConfiguration(companyPreferences);

  // Sync print config when company preferences load
  const prefsApplied = React.useRef(false);
  useEffect(() => {
    if (companyPreferences && !prefsApplied.current) {
      updatePrintConfig(companyPreferences);
      prefsApplied.current = true;
    }
  }, [companyPreferences, updatePrintConfig]);

  // State management
  const [selectedBankId, setSelectedBankId] = useState('');
  const [banks, setBanks] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [beneficiariesLoading, setBeneficiariesLoading] = useState(false);
  const [cheques, setCheques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [printOptionsOpen, setPrintOptionsOpen] = useState(false);
  const { snackbar, hideSnackbar, showSuccess, showError, showWarning } = useSnackbar();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 15 });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [individualPrinting, setIndividualPrinting] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [selectedCheque, setSelectedCheque] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [attachmentsDialogOpen, setAttachmentsDialogOpen] = useState(false);
  const [attachmentsCheque, setAttachmentsCheque] = useState(null);
  const [nextChequeNumber, setNextChequeNumber] = useState('');
  const [chequeNumberLoading, setChequeNumberLoading] = useState(false);
  const [hasNumericHistory, setHasNumericHistory] = useState(false); // New state for filtering: 'all', 'pending', 'printed' - default to pending for printing workflow
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelChequeId, setCancelChequeId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [clearingDialogOpen, setClearingDialogOpen] = useState(false);
  const [clearingChequeIds, setClearingChequeIds] = useState([]);
  const [unclearDialogOpen, setUnclearDialogOpen] = useState(false);
  const [historyImportOpen, setHistoryImportOpen] = useState(false);
  const [unclearChequeIds, setUnclearChequeIds] = useState([]);

  const {
    loading: chequeLoading,
    createCheque,
    deleteCheque,
    cancelCheque,
    clearCheques,
    unclearCheques,
    signCheques,
    unsignCheques,
    printSingleCheque,
    printBulkCheques
  } = useCheques(showSuccess, showError);

  // Optional "Signed Status" module — gated by company preference
  const signedStatusEnabled = Boolean(companyPreferences?.enableSignedStatus);
  // Optional "Payment Voucher Printing" module — gated by company preference (icon hidden when off)
  const voucherPrintingEnabled = Boolean(companyPreferences?.enableVoucherPrinting);
  // Optional "Cheque Collection" module — adds the Collected filter (collected cheques can still be cleared/cancelled)
  const chequeCollectionEnabled = Boolean(companyPreferences?.enableChequeCollection);
  const [voucherPrinting, setVoucherPrinting] = useState({});
  const { user: currentUser } = useEasyCheck();
  const isAdmin = currentUser?.role === 'Admin';
  // Zoho Books "API Pull" import — only asked for on plans with zohoIntegration; the backend decides
  // availability (method = API_PULL, connected, module permission). Webhook companies never see the button.
  const zohoPlanAllowed = canAccess(currentUser?.planId, 'zohoIntegration');
  const { data: zohoSettings } = useQuery({
    queryKey: ['zohoSettings'],
    queryFn: () => zohoService.getSettings(),
    enabled: zohoPlanAllowed,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const zohoImportAvailable = zohoPlanAllowed && Boolean(zohoSettings?.importAvailable);
  const [zohoImportOpen, setZohoImportOpen] = useState(false);
  const bankService = useBankService();
  const beneficiaryService = useBeneficiaryService();

  // Snackbar functionality is now handled by useSnackbar hook

  // Form controls for adding cheques
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      bankId: '',
      beneficiaryName: '',
      chequeNumber: '',
      chqAmount: '',
      chqDate: new Date(),
      remarks: ''
    }
  });

  const watchBankId = watch('bankId');
  const selectedCurrency = useMemo(
    () => banks.find((b) => b.id === selectedBankId)?.currency || 'AED',
    [banks, selectedBankId]
  );

  // Fetch next cheque number suggestion for a bank
  const fetchNextChequeNumber = async (bankId) => {
    if (!bankId) return;
    setChequeNumberLoading(true);
    try {
      const result = await chequeService.getNextChequeNumber(bankId);
      if (result.success && result.data) {
        setNextChequeNumber(result.data.nextChequeNumber || '');
        setHasNumericHistory(result.data.hasNumericHistory || false);
        if (result.data.nextChequeNumber) {
          setValue('chequeNumber', result.data.nextChequeNumber);
        }
      } else {
        setNextChequeNumber('');
        setHasNumericHistory(false);
      }
    } catch (error) {
      console.error('Error fetching next cheque number:', error);
      setNextChequeNumber('');
      setHasNumericHistory(false);
    } finally {
      setChequeNumberLoading(false);
    }
  };

  // Load banks and beneficiaries on component mount
  useEffect(() => {
    loadBanks();
    loadBeneficiaries();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load cheques when bank selection changes
  useEffect(() => {
    if (selectedBankId) {
      loadCheques();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBankId, paginationModel.page, paginationModel.pageSize]);

  // Update form when bank selection changes
  useEffect(() => {
    if (watchBankId) {
      setSelectedBankId(watchBankId);
    }
  }, [watchBankId, banks]);

  // Prevent body scrollbar to ensure only DataGrid scrolls
  useEffect(() => {
    // Hide all possible scrollbars when component mounts
    document.body.style.setProperty('overflow', 'hidden', 'important');
    document.documentElement.style.setProperty('overflow', 'hidden', 'important');
    document.body.style.setProperty('overflow-x', 'hidden', 'important');
    document.body.style.setProperty('overflow-y', 'hidden', 'important');
    document.documentElement.style.setProperty('overflow-x', 'hidden', 'important');
    document.documentElement.style.setProperty('overflow-y', 'hidden', 'important');
    
    // Also target the main app container
    const appContainer = document.getElementById('root');
    if (appContainer) {
      appContainer.style.setProperty('overflow', 'hidden', 'important');
    }
    
    // Restore scrollbar when component unmounts
    return () => {
      document.body.style.removeProperty('overflow');
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow-x');
      document.body.style.removeProperty('overflow-y');
      document.documentElement.style.removeProperty('overflow-x');
      document.documentElement.style.removeProperty('overflow-y');
      if (appContainer) {
        appContainer.style.removeProperty('overflow');
      }
    };
  }, []);

  // Load available banks
  const loadBanks = async () => {
    try {
      setLoading(true);
      const result = await bankService.getAllBanks(1, 10000);
      
      if (result.success || result.Success) {
        const data = result.data || result.Data || [];
        const banksData = Array.isArray(data) ? data.map(bank => ({
          id: bank.Id || bank.id,
          accountNumber: bank.AccountNumber || bank.accountNumber,
          accountName: bank.AccountName || bank.accountName,
          currency: bank.Currency || bank.currency,
          balance: bank.Balance || bank.balance || 0,
          companyId: bank.CompanyId || bank.companyId,
          isActive: bank.IsActive !== undefined ? bank.IsActive : bank.isActive,
          isDefault: bank.IsDefault !== undefined ? bank.IsDefault : bank.isDefault,
          hasChequeTemplate: bank.HasChequeTemplate !== undefined ? bank.HasChequeTemplate : bank.hasChequeTemplate || false,
          createdDate: bank.CreatedDate || bank.createdDate,
          modifiedDate: bank.ModifiedDate || bank.modifiedDate,
          createdUser: bank.CreatedUser || bank.createdUser,
          modifiedUser: bank.ModifiedUser || bank.modifiedUser
        })) : [];
        
        setBanks(banksData);
        
        if (banksData.length === 0) {
          showWarning('No bank accounts found. Please add a bank account first.');
        }
      } else {
        setBanks([]);
        showError('Failed to load banks. Please try again.');
      }
    } catch (error) {
      showError('Failed to load banks');
      setBanks([]);
    } finally {
      setLoading(false);
    }
  };

  // Load available beneficiaries
  const loadBeneficiaries = async () => {
    try {
      setBeneficiariesLoading(true);
      const result = await beneficiaryService.getAllBeneficiaries(1, 10000);
      
      if (result.success || result.Success) {
        const data = result.data || result.Data || [];
        const beneficiariesData = Array.isArray(data) ? data.map(beneficiary => ({
          id: beneficiary.Id || beneficiary.id,
          beneficiaryName: beneficiary.BeneficiaryName || beneficiary.beneficiaryName,
          companyId: beneficiary.CompanyId || beneficiary.companyId,
          isActive: beneficiary.IsActive !== undefined ? beneficiary.IsActive : beneficiary.isActive,
          createdDate: beneficiary.CreatedDate || beneficiary.createdDate,
          createdUser: beneficiary.CreatedUser || beneficiary.createdUser,
          modifiedDate: beneficiary.ModifiedDate || beneficiary.modifiedDate,
          modifiedUser: beneficiary.ModifiedUser || beneficiary.modifiedUser,
        })).filter(b => b.isActive !== false) : []; // Only show active beneficiaries
        
        setBeneficiaries(beneficiariesData);
      } else {
        setBeneficiaries([]);
        console.warn('Failed to load beneficiaries:', result.message);
      }
    } catch (error) {
      console.error('Failed to load beneficiaries:', error);
      setBeneficiaries([]);
    } finally {
      setBeneficiariesLoading(false);
    }
  };

  // Load cheques for selected bank
  const loadCheques = async () => {
    if (!selectedBankId) {
      setCheques([]);
      return;
    }

    try {
      setLoading(true);
      
      // Always load ALL records for client-side filtering
      const response = await chequeService.getPrintedCheques({
        pageNumber: 1,
        pageSize: 10000,
        bankId: selectedBankId,
        status: ''
      });
      
      if (response.success && response.data) {
        const mappedCheques = response.data.map(cheque => ({
          id: cheque.Id || cheque.id,
          chequeNumber: cheque.ChequeNumber || cheque.chequeNumber,
          bankId: cheque.BankId || cheque.bankId,
          bankName: cheque.BankName || cheque.bankName,
          companyId: cheque.CompanyId || cheque.companyId,
          beneficiaryName: cheque.BeneficiaryName || cheque.beneficiaryName,
          chqAmount: cheque.ChqAmount || cheque.chqAmount,
          amountInWords: cheque.AmountInWords || cheque.amountInWords,
          chqDate: (cheque.ChqDate || cheque.chqDate || '').split('T')[0],
          printDate: cheque.PrintDate || cheque.printDate,
          remarks: cheque.Remarks || cheque.remarks,
          status: cheque.Status || cheque.status,
          clearedDate: cheque.ClearedDate || cheque.clearedDate || null,
          isActive: cheque.IsActive !== undefined ? cheque.IsActive : cheque.isActive,
          createdAt: cheque.CreatedAt || cheque.createdAt,
          updatedAt: cheque.UpdatedAt || cheque.updatedAt,
          createdBy: cheque.CreatedBy || cheque.createdBy,
          updatedBy: cheque.UpdatedBy || cheque.updatedBy
        }));
        
        setCheques(mappedCheques);
      } else {
        setCheques([]);
      }
    } catch (error) {
      setCheques([]);
      showError('Failed to load cheques');
    } finally {
      setLoading(false);
    }
  };

  // Utility to format date as YYYY-MM-DD
  const formatDate = (date) => {
    if (!date) return '';
    if (typeof date === 'string') return date.split('T')[0];
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Handle add cheque
  const onSubmit = async (data) => {
    if (!selectedBankId) {
      showError('Please select a bank account first');
      return;
    }

    try {
      // Single cheque creation - format cheque number before submitting
      const formattedChequeNumber = formatChequeNumber(data.chequeNumber);
      const payload = {
        bankId: selectedBankId,
        beneficiaryName: data.beneficiaryName,
        chqAmount: parseFloat(data.chqAmount),
        chqDate: formatDate(data.chqDate),
        remarks: data.remarks || '',
        chequeNumber: formattedChequeNumber || ''
      };

      const result = await createCheque(payload);

      if (!result || !result.success) {
        // Keep dialog open on failure; on duplicate, refresh suggested number (auto-fills field).
        if (result?.message && /already exists/i.test(result.message)) {
          fetchNextChequeNumber(selectedBankId);
        }
        return;
      }

      reset({
        bankId: '',
        beneficiaryName: '',
        chequeNumber: '',
        chqAmount: '',
        chqDate: new Date(),
        remarks: ''
      });
      setAddModalOpen(false);
      await loadCheques();
      fetchNextChequeNumber(selectedBankId);

    } catch (error) {
      console.error('Error creating cheque:', error);
      showError(`Failed to create cheque: ${error.message || 'Unknown error'}`);
    }
  };

  // Handle delete cheque
  const handleDeleteCheque = async (chequeId) => {
    if (window.confirm('Are you sure you want to delete this cheque?')) {
      try {
        // The deleteCheque hook handles success/error notifications and throws on error
        await deleteCheque(chequeId);
        
        // If we reach here, it was successful
        // Reload data immediately
        await loadCheques();
      } catch (error) {
        // Error is already handled by the hook (notification shown)
        console.error('Error deleting cheque:', error);
      }
    }
  };

  // Handle cancel cheque
  const handleOpenCancelDialog = (chequeId) => {
    setCancelChequeId(chequeId);
    setCancelReason('');
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!cancelReason.trim()) return;
    try {
      const success = await cancelCheque(cancelChequeId, cancelReason.trim());
      if (success) {
        setCancelDialogOpen(false);
        setCancelChequeId(null);
        setCancelReason('');
        await loadCheques();
      }
    } catch (error) {
      console.error('Error cancelling cheque:', error);
    }
  };

  // Handle clearing cheques
  const handleOpenClearingDialog = (chequeIds) => {
    setClearingChequeIds(chequeIds);
    setClearingDialogOpen(true);
  };

  const handleConfirmClearing = async (clearedDate) => {
    const year = clearedDate.getFullYear();
    const month = String(clearedDate.getMonth() + 1).padStart(2, '0');
    const day = String(clearedDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    const success = await clearCheques(clearingChequeIds, dateStr);
    if (success) {
      setClearingDialogOpen(false);
      setClearingChequeIds([]);
      setSelectedRows([]);
      await loadCheques();
    }
  };

  // Handle reversing clearing (marking cheques as uncleared)
  const handleOpenUnclearDialog = (chequeIds) => {
    setUnclearChequeIds(chequeIds);
    setUnclearDialogOpen(true);
  };

  const handleConfirmUnclearing = async () => {
    const success = await unclearCheques(unclearChequeIds);
    if (success) {
      setUnclearDialogOpen(false);
      setUnclearChequeIds([]);
      setSelectedRows([]);
      await loadCheques();
    }
  };

  // Handle Signed Status module actions (no dialog — direct status toggle)
  const handleMarkSigned = async (chequeIds) => {
    const success = await signCheques(chequeIds);
    if (success) {
      setSelectedRows([]);
      await loadCheques();
    }
  };

  const handleMarkUnsigned = async (chequeIds) => {
    const success = await unsignCheques(chequeIds);
    if (success) {
      setSelectedRows([]);
      await loadCheques();
    }
  };

  // Handle individual cheque print with options
  const handlePrintIndividualCheque = async (chequeId) => {
    setIndividualPrinting(prev => ({ ...prev, [chequeId]: true }));
    try {
      await printSingleCheque(chequeId, printConfig);
      // Reload data immediately
      await loadCheques(); // Reload to update status
    } catch (error) {
      // Error handling is done in the hook
    } finally {
      setIndividualPrinting(prev => ({ ...prev, [chequeId]: false }));
    }
  };

  // Print a payment voucher for one cheque (voucher module). Independent of cheque printing:
  // no status change, so no loadCheques() afterwards.
  const handlePrintVoucher = async (chequeId) => {
    setVoucherPrinting(prev => ({ ...prev, [chequeId]: true }));
    try {
      const result = await voucherService.printVoucher(chequeId);
      if (!result.success) {
        showError(result.message || 'Failed to print voucher');
      }
    } finally {
      setVoucherPrinting(prev => ({ ...prev, [chequeId]: false }));
    }
  };

  // Handle bulk print selected cheques with options
  const handlePrintSelectedCheques = async () => {
    if (selectedRows.length === 0) {
      showWarning('Please select cheques to print');
      return;
    }

    setPrinting(true);
    try {
      await printBulkCheques(selectedRows, printConfig);
      setSelectedRows([]); // Clear selection
      // Reload data immediately
      await loadCheques(); // Reload to update status
    } catch (error) {
      // Error handling is done in the hook
    } finally {
      setPrinting(false);
    }
  };

  // Select all cheques (based on current filter)
  const handleSelectAllCheques = () => {
    const allVisibleChequeIds = rows.map(c => c.id);
    setSelectedRows(allVisibleChequeIds);
  };

  // DataGrid column definitions with enhanced filtering
  const columns = [
    {
      field: 'chequeNumber',
      headerName: 'Cheque #',
      width: 130,
      filterable: true,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
          <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'beneficiaryName',
      headerName: 'Beneficiary',
      flex: 2,
      minWidth: 140,
      filterable: true,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
          <Typography variant="body2" fontWeight="medium">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'chqAmount',
      headerName: 'Amount',
      width: 120,
      filterable: true,
      sortable: true,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%', width: '100%' }}>
          <Typography variant="body2" fontWeight="bold" color="primary">
            {params.value != null ? formatCurrencyAmount(params.value, selectedCurrency, { withSymbol: false }) : ''}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'chqDate',
      headerName: 'Chq Date',
      width: 110,
      filterable: true,
      sortable: true,
      type: 'date',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
          <Typography variant="body2">
            {params.value ? formatDateForDisplay(params.value) : '-'}
          </Typography>
        </Box>
      ),
      valueGetter: (value) => {
        return value ? new Date(value) : null;
      }
    },
    {
      field: 'printDate',
      headerName: 'Print Date',
      width: 110,
      filterable: true,
      sortable: true,
      type: 'date',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            {params.value ? formatDateForDisplay(params.value) : '-'}
          </Typography>
        </Box>
      ),
      valueGetter: (value) => {
        return value ? new Date(value) : null;
      }
    },
    {
      field: 'clearedDate',
      headerName: 'Cleared Date',
      width: 115,
      filterable: true,
      sortable: true,
      type: 'date',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            {params.value ? formatDateForDisplay(params.value) : '\u2014'}
          </Typography>
        </Box>
      ),
      valueGetter: (value) => {
        return value ? new Date(value) : null;
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      filterable: true,
      sortable: true,
      type: 'singleSelect',
      valueOptions: ['Pending', 'Printed', 'Signed', 'Collected', 'Cancelled', 'Cleared'],
      renderCell: (params) => {
        const statusStyles = {
          Printed: {
            bg: themeUtils.colors.blue['blue-100'],
            color: themeUtils.colors.blue['blue-700'],
          },
          Pending: {
            bg: '#FFF9C4',
            color: '#F57F17',
          },
          Signed: {
            bg: '#EDE7F6',
            color: '#5E35B1',
          },
          Collected: {
            bg: '#E0F2F1',
            color: '#00695C',
          },
          Cleared: {
            bg: '#E8F5E9',
            color: '#2E7D32',
          },
          Cancelled: {
            bg: '#FFEBEE',
            color: '#C62828',
          },
        };
        const style = statusStyles[params.value] || statusStyles.Pending;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
            <Chip
              label={params.value}
              size="small"
              variant="filled"
              sx={{
                backgroundColor: style.bg,
                color: style.color,
                fontWeight: 600,
                fontSize: '0.75rem',
                height: 24,
              }}
            />
          </Box>
        );
      },
    },
    {
      field: 'remarks',
      headerName: 'Remarks',
      flex: 1.5,
      minWidth: 100,
      filterable: true,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            {params.value || '-'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      // wide enough for all icons incl. Delete; each optional module (Signed, Voucher) adds one icon (~35px)
      width: 285 + (signedStatusEnabled ? 35 : 0) + (voucherPrintingEnabled ? 35 : 0),
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={params.row.status === 'Printed' ? 'View PDF' : 'Print & View'}>
              <span>
                <EasyCheckButton
                  buttonType="icon"
                  onClick={() => handlePrintIndividualCheque(params.row.id)}
                  size="small"
                  disabled={params.row.status === 'Cancelled'}
                  loading={individualPrinting[params.row.id]}
                >
                  <PrintIcon fontSize="small" />
                </EasyCheckButton>
              </span>
            </Tooltip>
            {voucherPrintingEnabled && (
              <Tooltip title="Print Voucher">
                <span>
                  <EasyCheckButton
                    buttonType="icon"
                    onClick={() => handlePrintVoucher(params.row.id)}
                    size="small"
                    disabled={params.row.status === 'Cancelled'}
                    loading={voucherPrinting[params.row.id]}
                  >
                    <VoucherIcon fontSize="small" />
                  </EasyCheckButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Mark as Cleared">
              <span>
                <EasyCheckIconButton
                  variant="success"
                  onClick={() => handleOpenClearingDialog([params.row.id])}
                  size="small"
                  disabled={!['Printed', 'Signed', 'Collected'].includes(params.row.status)}
                >
                  <CheckCircleIcon fontSize="small" />
                </EasyCheckIconButton>
              </span>
            </Tooltip>
            <Tooltip title="Reverse Clearing">
              <span>
                <EasyCheckIconButton
                  variant="primary"
                  onClick={() => handleOpenUnclearDialog([params.row.id])}
                  size="small"
                  disabled={params.row.status !== 'Cleared'}
                >
                  <ReverseClearIcon fontSize="small" />
                </EasyCheckIconButton>
              </span>
            </Tooltip>
            {signedStatusEnabled && params.row.status === 'Printed' && (
              <Tooltip title="Mark as Signed">
                <span>
                  <EasyCheckIconButton
                    variant="primary"
                    onClick={() => handleMarkSigned([params.row.id])}
                    size="small"
                  >
                    <SignedIcon fontSize="small" />
                  </EasyCheckIconButton>
                </span>
              </Tooltip>
            )}
            {signedStatusEnabled && params.row.status === 'Signed' && (
              <Tooltip title="Revert to Printed">
                <span>
                  <EasyCheckIconButton
                    variant="primary"
                    onClick={() => handleMarkUnsigned([params.row.id])}
                    size="small"
                  >
                    <UndoSignIcon fontSize="small" />
                  </EasyCheckIconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Cancel Cheque">
              <span>
                <EasyCheckIconButton
                  variant="danger"
                  onClick={() => handleOpenCancelDialog(params.row.id)}
                  size="small"
                  disabled={!['Printed', 'Signed', 'Collected'].includes(params.row.status)}
                >
                  <CancelIcon fontSize="small" />
                </EasyCheckIconButton>
              </span>
            </Tooltip>
            <Tooltip title="Attachments">
              <span>
                <EasyCheckIconButton
                  variant="primary"
                  onClick={() => {
                    setAttachmentsCheque(params.row);
                    setAttachmentsDialogOpen(true);
                  }}
                  size="small"
                >
                  <AttachFileIcon fontSize="small" />
                </EasyCheckIconButton>
              </span>
            </Tooltip>
            <Tooltip title="Edit">
              <span>
                <EasyCheckIconButton
                  variant="primary"
                  onClick={() => {
                    const chequeToEdit = {
                      ...params.row,
                      chqDate: params.row.chqDate ? new Date(params.row.chqDate) : new Date(),
                    };
                    reset(chequeToEdit);
                    setEditMode(true);
                    setSelectedCheque(chequeToEdit);
                    setAddModalOpen(true);
                  }}
                  size="small"
                  disabled={params.row.status === 'Cancelled'}
                >
                  <EditIcon fontSize="small" />
                </EasyCheckIconButton>
              </span>
            </Tooltip>
            {isAdmin && (
              <Tooltip title="Delete">
                <span>
                  <EasyCheckIconButton
                    variant="danger"
                    onClick={() => handleDeleteCheque(params.row.id)}
                    size="small"
                    disabled={params.row.status === 'Cancelled'}
                  >
                    <DeleteIcon fontSize="small" />
                  </EasyCheckIconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Box>
      ),
    },
  ];

  const CustomColumnMenu = (props) => {
    return (
      <GridColumnMenu
        {...props}
        slots={{
          columnMenuFilterItem: null,
        }}
      />
    );
  };

  const handleExportExcel = () => {
    const statusLabels = { pending: 'Pending', printed: 'Printed', uncleared: 'Uncleared', signed: 'Signed', collected: 'Collected', cleared: 'Cleared', cancelled: 'Cancelled' };
    const statusLabel = statusLabels[statusFilter] || statusFilter;

    const columns = [
      { header: 'Cheque #', key: 'chequeNumber', type: 'text' },
      { header: 'Beneficiary', key: 'beneficiaryName', type: 'text', width: 30 },
      { header: 'Amount', key: 'chqAmount', type: 'amount', decimals: getCurrencyDecimals(selectedCurrency) },
      { header: 'Date', key: 'chqDate', type: 'date' },
      { header: 'Print Date', key: 'printDate', type: 'date' },
      { header: 'Cleared Date', key: 'clearedDate', type: 'date' },
      { header: 'Status', key: 'status', type: 'text' },
      { header: 'Remarks', key: 'remarks', type: 'text', width: 25 },
    ];

    const data = filteredRows.map(row => ({
      chequeNumber: row.chequeNumber,
      beneficiaryName: row.beneficiaryName,
      chqAmount: Number(row.chqAmount || 0),
      chqDate: row.chqDate ? formatDateForDisplay(row.chqDate) : '',
      printDate: row.printDate ? formatDateForDisplay(row.printDate) : '',
      clearedDate: row.clearedDate ? formatDateForDisplay(row.clearedDate) : '',
      status: row.status,
      remarks: row.remarks || '',
    }));

    const totalAmount = filteredRows.reduce((sum, row) => sum + (Number(row.chqAmount) || 0), 0);

    exportToExcel({
      title: `Cheque List - ${statusLabel}`,
      fileName: 'EasyCheck_Cheques.xlsx',
      sheetName: 'Cheques',
      columns,
      data,
      parameters: [{ label: 'Status Filter', value: statusLabel }],
      totals: { 'Amount': totalAmount },
    });
  };

  const rows = useMemo(() => {
    if (!Array.isArray(cheques)) return [];

    // First, filter based on status
    let filteredCheques = cheques;
    if (statusFilter === 'pending') {
      filteredCheques = cheques.filter(cheque => cheque.status === 'Pending');
    } else if (statusFilter === 'printed') {
      filteredCheques = cheques.filter(cheque => cheque.status === 'Printed' || cheque.status === 'Cleared');
    } else if (statusFilter === 'uncleared') {
      filteredCheques = cheques.filter(cheque => cheque.status === 'Printed');
    } else if (statusFilter === 'signed') {
      filteredCheques = cheques.filter(cheque => cheque.status === 'Signed');
    } else if (statusFilter === 'collected') {
      filteredCheques = cheques.filter(cheque => cheque.status === 'Collected');
    } else if (statusFilter === 'cleared') {
      filteredCheques = cheques.filter(cheque => cheque.status === 'Cleared');
    } else if (statusFilter === 'cancelled') {
      filteredCheques = cheques.filter(cheque => cheque.status === 'Cancelled');
    }

    // Then map to row format
    return filteredCheques.map((cheque, index) => ({
      id: cheque.id || index,
      chequeNumber: cheque.chequeNumber,
      beneficiaryName: cheque.beneficiaryName,
      chqAmount: cheque.chqAmount,
      chqDate: cheque.chqDate,
      printDate: cheque.printDate,
      status: cheque.status,
      remarks: cheque.remarks,
      ...cheque
    }));
  }, [cheques, statusFilter]);

  // Apply global search filter on top of status-filtered rows
  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows;
    const query = searchQuery.toLowerCase();
    return rows.filter(row =>
      (row.chequeNumber && row.chequeNumber.toLowerCase().includes(query)) ||
      (row.beneficiaryName && row.beneficiaryName.toLowerCase().includes(query)) ||
      (row.remarks && row.remarks.toLowerCase().includes(query)) ||
      (row.chqAmount != null && String(row.chqAmount).includes(query))
    );
  }, [rows, searchQuery]);

  return (
    <PageContainer>
      {/* Bank Selection Header */}
      <HeaderSection>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h4" fontWeight={700} color="primary">
            Cheque Management
          </Typography>
          {selectedBankId && statusFilter !== 'all' && (
            <Chip
              label={`${statusFilter === 'pending' ? 'Pending' : statusFilter === 'printed' ? 'Printed' : statusFilter === 'uncleared' ? 'Uncleared' : statusFilter === 'signed' ? 'Signed' : statusFilter === 'collected' ? 'Collected' : statusFilter === 'cleared' ? 'Cleared' : 'Cancelled'} Only (${rows.length})`}
              color={statusFilter === 'pending' ? 'warning' : statusFilter === 'printed' ? 'primary' : statusFilter === 'uncleared' ? 'warning' : statusFilter === 'signed' ? 'primary' : statusFilter === 'collected' ? 'success' : statusFilter === 'cleared' ? 'success' : 'error'}
              size="small"
              variant="outlined"
              sx={{ ml: 1 }}
            />
          )}
          <FormControl size="small" sx={{ minWidth: 320, my: 1.5 }}>
            <InputLabel sx={{ fontSize: '0.875rem' }}>Select Bank Account</InputLabel>
            <Select
              value={selectedBankId}
              label="Select Bank Account"
              onChange={(e) => {
                setSelectedBankId(e.target.value);
                fetchNextChequeNumber(e.target.value);
              }}
              sx={{ 
                borderRadius: 0,
                fontSize: '0.875rem',
                '& .MuiSelect-select': {
                  py: 1
                },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0px !important',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '0px !important',
                }
              }}
            >
              {banks.map((bank) => (
                <MenuItem key={bank.id} value={bank.id} sx={{ fontSize: '0.875rem' }}>
                  {bank.accountNumber} - {bank.accountName} ({bank.currency})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Action buttons - only visible when a bank is selected */}
        {selectedBankId && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <EasyCheckButton
              buttonType="action"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                reset({
                  bankId: '',
                  beneficiaryName: '',
                  chequeNumber: '',
                  chqAmount: '',
                  chqDate: new Date(),
                  remarks: ''
                });
                setEditMode(false);
                setSelectedCheque(null);
                fetchNextChequeNumber(selectedBankId);
                setAddModalOpen(true);
              }}
              size="small"
            >
              ADD
            </EasyCheckButton>
            
            {cheques.length > 0 && (
              <EasyCheckButton
                buttonType="action"
                variant="outlined"
                onClick={handleSelectAllCheques}
                size="small"
              >
                ALL
              </EasyCheckButton>
            )}

            <EasyCheckButton
              buttonType="action"
              variant="contained"
              startIcon={<PrintIcon />}
              loading={printing}
              onClick={handlePrintSelectedCheques}
              disabled={selectedRows.length === 0}
              size="small"
            >
              PRINT ({selectedRows.length})
            </EasyCheckButton>

            <Tooltip title="Configure Print Options">
              <IconButton
                size="small"
                onClick={() => setPrintOptionsOpen(true)}
                sx={{ 
                  border: '1px solid',
                  borderColor: 'grey.300',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'primary.50'
                  }
                }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </HeaderSection>

      {/* Print Configuration Display */}
      <Box sx={{ mb: 0.5 }}>
        <PrintConfigurationDisplay 
          printConfig={printConfig}
          variant="default"
          size="small"
          showTitle={false}
        />
      </Box>

      {/* DataGrid with Advanced Filtering */}
      <Box sx={{ 
        flexGrow: 1, 
        minHeight: 0,
        maxHeight: 'calc(100vh - 200px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        backgroundColor: 'white',
      }}>
                      <EasyCheckDataGridBasic
          rows={filteredRows}
          columns={columns}
          loading={loading}
          checkboxSelection={true}
          disableRowSelectionOnClick
          disableColumnFilter
          rowHeight={64}
          paginationMode="client"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowSelectionModel={selectedRows}
          onRowSelectionModelChange={(newSelection) => {
            setSelectedRows(newSelection);
          }}
          isRowSelectable={(params) => true}
          getRowClassName={(params) =>
            params.row.status === 'Pending' ? 'pending-cheque' : ''
          }
          slots={{
            toolbar: CustomToolbar,
            columnMenu: CustomColumnMenu,
            noRowsOverlay: () => (
              <Stack
                height="100%"
                alignItems="center"
                justifyContent="center"
                spacing={2}
                sx={{ py: 2 }}
              >
                <ChequeIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                <Typography variant="h6" color="text.secondary">
                  {selectedBankId ?
                    (statusFilter === 'all' ? 'No cheques found' : `No ${statusFilter} cheques found`) :
                    'Select a bank account to view cheques'
                  }
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  {selectedBankId ?
                    (statusFilter === 'all' ? 'Add your first cheque to get started' : `Try selecting 'All' to see other cheques`) :
                    'Choose a bank account from the dropdown above'
                  }
                </Typography>
              </Stack>
            ),
          }}
          slotProps={{
            toolbar: {
              searchQuery,
              onSearchChange: setSearchQuery,
              statusFilter,
              onStatusFilterChange: (newValue) => {
                setStatusFilter(newValue);
                setSelectedRows([]);
              },
              selectedRows,
              onClearingDialog: handleOpenClearingDialog,
              onUnclearDialog: handleOpenUnclearDialog,
              onSignCheques: handleMarkSigned,
              onUnsignCheques: handleMarkUnsigned,
              signedStatusEnabled,
              chequeCollectionEnabled,
              onExportExcel: handleExportExcel,
              onImportHistory: () => setHistoryImportOpen(true),
              zohoImportAvailable,
              onImportZoho: () => setZohoImportOpen(true),
            },
          }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 15 },
            },
          }}
          columnHeaderHeight={56}
          pageSizeOptions={[10, 15, 25, 50]}
          disableVirtualization
          pagination
          sx={{ 
            height: '100%',
            width: '100%',
            maxHeight: 'calc(100vh - 220px)',
            border: 'none',
            overflow: 'hidden',
            '& .MuiDataGrid-main': {
              overflow: 'hidden',
              maxHeight: 'calc(100vh - 260px)',
            },
            '& .MuiDataGrid-virtualScroller': {
              overflow: 'auto !important',
              maxHeight: 'calc(100vh - 300px)',
            },
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f5f5f5',
              borderRight: 'none !important',
              borderLeft: 'none !important',
              padding: '16px 12px',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#F7F9FC',
              borderBottom: '1px solid #e8e8e8 !important',
              borderTop: 'none !important',
              height: '56px',
              minHeight: '56px !important',
            },
            '& .MuiDataGrid-columnHeader': {
              borderRight: 'none !important',
              borderLeft: 'none !important',
              borderBottom: 'none !important',
              color: '#000000 !important',
              display: 'flex !important',
              alignItems: 'center !important',
              justifyContent: 'flex-start !important',
              height: '56px !important',
              fontWeight: '600 !important',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: '600 !important',
            },
            '& .MuiDataGrid-footerContainer': {
              minHeight: '52px',
              maxHeight: '52px',
            },
          }}
        />
      </Box>

      {/* Add Cheque Modal */}
      <EasyCheckDialog
        open={addModalOpen}
        onClose={() => {
          setAddModalOpen(false);
          setEditMode(false);
          setSelectedCheque(null);
          reset({
            bankId: '',
            beneficiaryName: '',
            chequeNumber: '',
            chqAmount: '',
            chqDate: new Date(),
            remarks: ''
          });
        }}
        title={editMode ? 'Edit Cheque' : 'Add New Cheque'}
        titleIcon={editMode ? <EditIcon /> : <AddIcon />}
        variant="form"
        maxWidth="md"
        actions={
          <>
            <EasyCheckButton 
              buttonType="form"
              variant="outlined"
              onClick={() => {
                setAddModalOpen(false);
                setEditMode(false);
                setSelectedCheque(null);
                reset({
                  bankId: '',
                  beneficiaryName: '',
                  chequeNumber: '',
                  chqAmount: '',
                  chqDate: new Date(),
                  remarks: ''
                });
              }}
            >
              Cancel
            </EasyCheckButton>
            <EasyCheckButton
              buttonType="form"
              type="submit"
              form="add-cheque-form"
              loading={chequeLoading}
            >
              {editMode ? 'Update Cheque' : 'Add Cheque'}
            </EasyCheckButton>
          </>
        }
      >
        <form id="add-cheque-form" onSubmit={handleSubmit((data) => {
          const payload = {
            ...data,
            chqDate: formatDate(data.chqDate),
          };
          if (editMode && selectedCheque) {
            chequeService.updatePrintedCheque(selectedCheque.id, payload)
              .then(() => {
                showSuccess('Cheque updated successfully');
                setAddModalOpen(false);
                setEditMode(false);
                setSelectedCheque(null);
                reset({
                  bankId: '',
                  beneficiaryName: '',
                  chequeNumber: '',
                  chqAmount: '',
                  chqDate: new Date(),
                  remarks: ''
                });
                loadCheques();
              })
              .catch(() => {
                showError('Failed to update cheque');
              });
          } else {
            onSubmit(payload);
          }
        })}>
          <Grid container spacing={3} sx={{ mt: 4 }}>
            {/* Cheque Number Field */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="chequeNumber"
                control={control}
                rules={{
                  required: 'Cheque number is required',
                  validate: (value) => {
                    const result = validateChequeNumber(value);
                    return result.isValid || result.message;
                  }
                }}
                render={({ field: { onChange, onBlur, value, ...field } }) => (
                  <EasyCheckTextField
                    {...field}
                    variant="form"
                    label="Cheque Number"
                    value={value || ''}
                    onChange={(e) => {
                      // Allow only digits
                      const numericValue = e.target.value.replace(/\D/g, '');
                      onChange(numericValue);
                    }}
                    onBlur={(e) => {
                      // Auto-format to 6 digits on blur
                      const formatted = formatChequeNumber(e.target.value);
                      if (formatted !== e.target.value) {
                        onChange(formatted);
                      }
                      onBlur(e);
                    }}
                    disabled={editMode}
                    error={!!errors.chequeNumber}
                    helperText={
                      errors.chequeNumber?.message ||
                      (editMode ? 'Cheque number cannot be changed after creation' :
                      nextChequeNumber ? `Suggested: ${nextChequeNumber}` :
                      'Enter cheque number (max 6 digits)')
                    }
                    inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                    InputProps={{
                      endAdornment: chequeNumberLoading ? (
                        <InputAdornment position="end">
                          <CircularProgress size={18} />
                        </InputAdornment>
                      ) : null
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="beneficiaryName"
                control={control}
                rules={{ 
                  required: 'Beneficiary name is required',
                  maxLength: { value: 200, message: 'Maximum 200 characters allowed' }
                }}
                render={({ field: { onChange, value, ...field } }) => (
                  <Autocomplete
                    {...field}
                    freeSolo
                    options={beneficiaries.map(b => b.beneficiaryName)}
                    value={value || ''}
                    onChange={(event, newValue) => {
                      onChange(newValue || '');
                    }}
                    onInputChange={(event, newInputValue) => {
                      onChange(newInputValue || '');
                    }}
                    loading={beneficiariesLoading}
                    renderInput={(params) => (
                      <EasyCheckTextField
                        {...params}
                        variant="form"
                        label="Pay to the Order of *"
                        error={!!errors.beneficiaryName}
                        helperText={errors.beneficiaryName?.message || 'Select from existing beneficiaries or type a new name'}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {beneficiariesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        {option}
                      </Box>
                    )}
                    filterOptions={(options, { inputValue }) => {
                      // Filter existing options
                      const filtered = options.filter(option =>
                        option.toLowerCase().includes(inputValue.toLowerCase())
                      );
                      
                      // If input doesn't match any existing option, allow manual entry
                      if (inputValue !== '' && !options.some(option => 
                        option.toLowerCase() === inputValue.toLowerCase()
                      )) {
                        filtered.push(inputValue);
                      }
                      
                      return filtered;
                    }}
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Controller
                name="chqAmount"
                control={control}
                rules={{
                  required: 'Amount is required',
                  min: { value: 0.01, message: 'Amount must be greater than 0' },
                  max: { value: 9999999999.99, message: 'Amount too large' },
                  validate: (v) => validateAmountPrecision(v, selectedCurrency)
                }}
                render={({ field }) => (
                  <EasyCheckTextField
                    {...field}
                    variant="form"
                    label="Amount *"
                    type="number"
                    inputProps={{ step: getAmountStep(selectedCurrency), min: 0.01 }}
                    error={!!errors.chqAmount}
                    helperText={errors.chqAmount?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="chqDate"
                control={control}
                rules={{ required: 'Date is required' }}
                render={({ field }) => (
                  <EasyCheckDatePicker
                    {...field}
                    datePickerVariant="form"
                    label="Cheque Date *"
                    format="dd-MM-yyyy"
                    error={!!errors.chqDate}
                    helperText={errors.chqDate?.message}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="remarks"
                control={control}
                render={({ field }) => (
                  <EasyCheckTextField
                    {...field}
                    variant="form"
                    label="Remarks"
                    multiline
                    rows={3}
                    helperText="Optional notes for this cheque"
                  />
                )}
              />
            </Grid>
            {editMode && selectedCheque?.id && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachFileIcon fontSize="small" /> Attachments
                </Typography>
                <ChequeAttachments
                  chequeId={selectedCheque.id}
                  compact
                  onError={showError}
                  onSuccess={showSuccess}
                />
              </Grid>
            )}
          </Grid>
        </form>
      </EasyCheckDialog>

      {/* Print Options Modal */}
      <PrintOptionsDialog
        open={printOptionsOpen}
        onClose={() => setPrintOptionsOpen(false)}
        printConfig={printConfig}
        onConfigChange={updatePrintConfig}
        title="Cheque Print Configuration"
      />

      {/* Cancel Cheque Confirmation Dialog */}
      <EasyCheckDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        title="Cancel Cheque"
        actions={
          <>
            <EasyCheckButton buttonType="form" variant="outlined" onClick={() => setCancelDialogOpen(false)}>
              Back
            </EasyCheckButton>
            <EasyCheckButton buttonType="form" color="error" onClick={handleConfirmCancel} disabled={!cancelReason.trim()}>
              Confirm Cancel
            </EasyCheckButton>
          </>
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This action cannot be undone. Please provide a reason for cancelling this cheque.
        </Typography>
        <EasyCheckTextField
          variant="form"
          label="Cancellation Reason"
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          multiline
          rows={3}
          required
          placeholder="e.g., Wrong amount, duplicate cheque, beneficiary change..."
          inputProps={{ maxLength: 500 }}
        />
      </EasyCheckDialog>

      {/* Mark as Cleared Dialog */}
      <ClearingDialog
        open={clearingDialogOpen}
        onClose={() => {
          setClearingDialogOpen(false);
          setClearingChequeIds([]);
        }}
        cheques={cheques.filter(c => clearingChequeIds.includes(c.id))}
        onConfirm={handleConfirmClearing}
        loading={chequeLoading}
      />

      {/* Reverse Clearing Dialog */}
      <EasyCheckDialog
        open={unclearDialogOpen}
        onClose={() => {
          setUnclearDialogOpen(false);
          setUnclearChequeIds([]);
        }}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReverseClearIcon color="primary" />
            Reverse Clearing
          </Box>
        }
        actions={
          <>
            <EasyCheckButton buttonType="form" variant="outlined" onClick={() => {
              setUnclearDialogOpen(false);
              setUnclearChequeIds([]);
            }}>
              Cancel
            </EasyCheckButton>
            <EasyCheckButton buttonType="form" color="primary" onClick={handleConfirmUnclearing} disabled={chequeLoading}>
              Confirm
            </EasyCheckButton>
          </>
        }
      >
        <Typography variant="body2" color="text.secondary">
          Are you sure you want to reverse the clearing for {unclearChequeIds.length} cheque(s)?
          This will set the status back to "Printed" and remove the cleared date.
        </Typography>
      </EasyCheckDialog>

      {/* Attachments Standalone Dialog */}
      <EasyCheckDialog
        open={attachmentsDialogOpen}
        onClose={() => {
          setAttachmentsDialogOpen(false);
          setAttachmentsCheque(null);
        }}
        title={attachmentsCheque ? `Attachments — #${attachmentsCheque.chequeNumber} — ${attachmentsCheque.beneficiaryName} — ${formatCurrencyAmount(attachmentsCheque.chqAmount, selectedCurrency, { withSymbol: false })} — ${attachmentsCheque.chqDate ? formatDateForDisplay(attachmentsCheque.chqDate) : ''}` : 'Cheque Attachments'}
        variant="form"
        maxWidth="sm"
      >
        {attachmentsCheque && (
          <ChequeAttachments
            chequeId={attachmentsCheque?.id}
            onError={showError}
            onSuccess={showSuccess}
          />
        )}
      </EasyCheckDialog>

      {/* History Import Dialog */}
      <ChequeHistoryImportDialog
        open={historyImportOpen}
        onClose={() => setHistoryImportOpen(false)}
        onImportComplete={() => {
          loadCheques();
          showSuccess('Cheque history imported successfully');
        }}
        banks={banks}
        beneficiaries={beneficiaries}
      />

      {/* Zoho Books import (API Pull method) */}
      {zohoImportAvailable && (
        <ZohoImportDialog
          open={zohoImportOpen}
          onClose={() => setZohoImportOpen(false)}
          onImported={() => {
            loadCheques();
            showSuccess('Cheques imported from Zoho Books');
          }}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transition="slide"
        showIcon={true}
      />
    </PageContainer>
  );
};

ChequePrintingForm.propTypes = {
  onSuccess: PropTypes.func
};

export default ChequePrintingForm; 